<template>
  <b-modal
      :visible="visible"
      id="admin-non-metrc-category-modal"
      :title="isEdit ? 'Edit Non-METRC Category' : 'Add Non-METRC Category'"
      @hidden="$emit('cancel')"
      hide-footer
      centered
      size="lg"
  >
    <b-form @submit.prevent="submitForm">
      <b-row>
        <b-col md="8">
          <b-form-group label="Name">
            <b-form-input
                v-model.trim="formData.name"
                type="text"
                :state="fieldValid('name')"
            />
            <b-form-invalid-feedback :state="fieldValid('name')">
              {{ formValidation.name }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Code">
            <b-form-input
                v-model.trim="formData.code"
                type="text"
                :disabled="isEdit"
                :state="fieldValid('code')"
            />
            <b-form-invalid-feedback :state="fieldValid('code')">
              {{ formValidation.code }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Definition">
        <b-form-textarea
            v-model.trim="formData.definition"
            rows="3"
            :state="fieldValid('definition')"
        />
        <b-form-invalid-feedback :state="fieldValid('definition')">
          {{ formValidation.definition }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-row>
        <b-col md="4">
          <b-form-group label="Sort Order">
            <b-form-input
                v-model="formData.sort_order"
                type="number"
                min="0"
                :state="fieldValid('sort_order')"
            />
            <b-form-invalid-feedback :state="fieldValid('sort_order')">
              {{ formValidation.sort_order }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col md="8">
          <b-form-group label="Browse Groups">
            <b-form-select
                v-model="formData.browse_group_ids"
                :options="browseGroupOptions"
                :select-size="Math.min(Math.max(browseGroupOptions.length, 4), 8)"
                :state="fieldValid('browse_group_ids')"
                multiple
            />
            <b-form-invalid-feedback :state="fieldValid('browse_group_ids')">
              {{ formValidation.browse_group_ids }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Facet Values">
        <b-form-select
            v-model="formData.facet_value_ids"
            :options="facetValueOptions"
            :select-size="10"
            :state="fieldValid('facet_value_ids')"
            multiple
        />
        <b-form-invalid-feedback :state="fieldValid('facet_value_ids')">
          {{ formValidation.facet_value_ids }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Admin Notes">
        <b-form-textarea
            v-model.trim="formData.admin_notes"
            rows="3"
            :state="fieldValid('admin_notes')"
        />
        <b-form-invalid-feedback :state="fieldValid('admin_notes')">
          {{ formValidation.admin_notes }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-button type="submit" variant="success" :disabled="formState.processing">
        Submit
        <b-spinner v-if="formState.processing" class="ml-1" small/>
      </b-button>
      <b-btn type="button" variant="secondary" @click.prevent="close">Cancel</b-btn>
    </b-form>
  </b-modal>
</template>

<script>
import {API_ROUTES} from "../api";

export default {
  name: "AdminNonMetrcCategoryModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    category: {
      type: Object,
      default: null
    },
    facetValues: {
      type: Array,
      default: () => []
    },
    browseGroups: {
      type: Array,
      default: () => []
    },
    facetDimensions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      formData: this.makeFormData(),
      formValidation: {},
      formState: {
        processing: false
      }
    }
  },
  computed: {
    isEdit() {
      return !!this.category;
    },
    browseGroupOptions() {
      return this.browseGroups
          .filter(group => !group.is_archived || this.formData.browse_group_ids.indexOf(group.id) >= 0)
          .map(group => ({
            value: group.id,
            text: `${group.display_name} (${group.group_key})${group.is_archived ? ' - Archived' : ''}`
          }));
    },
    facetValueOptions() {
      let selectedIds = this.formData.facet_value_ids;
      let options = this.facetDimensions.map(dimension => {
        let values = this.facetValues
            .filter(value => value.facet_key === dimension.value)
            .filter(value => !value.is_archived || selectedIds.indexOf(value.id) >= 0)
            .map(value => ({
              value: value.id,
              text: `${value.label} (${value.value_key})${value.is_archived ? ' - Archived' : ''}`
            }));

        return {
          label: dimension.text,
          options: values
        };
      }).filter(group => group.options.length > 0);

      let ungrouped = this.facetValues
          .filter(value => !this.facetDimensions.find(dimension => dimension.value === value.facet_key))
          .filter(value => !value.is_archived || selectedIds.indexOf(value.id) >= 0)
          .map(value => ({
            value: value.id,
            text: `${value.facet_key}: ${value.label} (${value.value_key})${value.is_archived ? ' - Archived' : ''}`
          }));

      if (ungrouped.length > 0) {
        options.push({
          label: 'Other',
          options: ungrouped
        });
      }

      return options;
    }
  },
  methods: {
    makeFormData() {
      return {
        name: this.category?.name ?? "",
        code: this.category?.code ?? "",
        definition: this.category?.definition ?? "",
        sort_order: this.category?.sort_order ?? 0,
        admin_notes: this.category?.admin_notes ?? "",
        facet_value_ids: [...(this.category?.facet_value_ids ?? [])],
        browse_group_ids: [...(this.category?.browse_group_ids ?? [])]
      };
    },
    close() {
      this.$root.$emit('bv::hide::modal', 'admin-non-metrc-category-modal');
    },
    fieldValid(field) {
      return typeof this.formValidation[field] !== "undefined" && this.formValidation[field].length > 0 ? false : null;
    },
    validationMessage(data, fallback) {
      return data?.message ?? fallback;
    },
    applyValidationErrors(data) {
      let validationErrors = {};
      if (typeof data?.errors !== 'undefined') {
        for (let fieldName in data.errors) {
          validationErrors[fieldName] = data.errors[fieldName].join(', ');
        }
      }
      this.formValidation = validationErrors;
    },
    payload() {
      let payload = {
        name: this.formData.name,
        definition: this.formData.definition || null,
        sort_order: this.formData.sort_order === "" ? null : Number(this.formData.sort_order),
        admin_notes: this.formData.admin_notes || null,
        facet_value_ids: this.formData.facet_value_ids,
        browse_group_ids: this.formData.browse_group_ids
      };

      if (!this.isEdit) {
        payload.code = this.formData.code;
      }

      return payload;
    },
    submitForm() {
      this.formState.processing = true;
      let url = this.isEdit
          ? `${API_ROUTES.ADMIN_NON_METRC_TAXONOMY_CATEGORIES}/${this.category.id}`
          : API_ROUTES.ADMIN_NON_METRC_TAXONOMY_CATEGORIES;

      this.$api.post(url, this.payload()).then(({data}) => {
        this.formValidation = {};
        this.$emit('success', data.category);
        this.close();
      }).catch(error => {
        let data = error.response?.data ?? {};
        this.$bvToast.toast(this.validationMessage(data, 'Failed to save category'), {
          title: 'Error!',
          variant: 'danger'
        });
        this.applyValidationErrors(data);
      }).finally(() => {
        this.formState.processing = false;
      });
    }
  }
}
</script>
